import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
//
import { useState } from 'react';
import { fileData, fileFormat, fileThumb } from './utils';
import DownloadButton from './download-button';
import Iconify from '../iconify';

// ----------------------------------------------------------------------

export default function FileThumbnail({
  file,
  tooltip,
  imageView,
  onDownload,
  sx,
  imgSx,
  imageError: externalImageError,
  setImageError: externalSetImageError,
  setValue,
  onClick,
}) {
  const { name = '', path = '', preview = '' } = fileData(file);
  
  // Use internal state if external state is not provided
  const [internalImageError, setInternalImageError] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const imageError = externalImageError !== undefined ? externalImageError : internalImageError;
  const setImageError = externalSetImageError || setInternalImageError;

  const handleImageError = () => {
    if (setValue) {
      setValue('file', '');
      setValue('images', [], { shouldValidate: true });
    }
    if (setImageError) {
      setImageError(true);
    }
  };
  
  // Enhanced file type detection
  // Priority: file.type (MIME type) > file format from path/preview
  const format = fileFormat(path || preview);
  
  // Check video: prioritize MIME type from file object
  const isVideo = 
    (file?.type && file.type.startsWith('video/')) || 
    format === 'video';
  
  // Check image: prioritize MIME type from file object
  const isImage = 
    !isVideo && (
      (file?.type && file.type.startsWith('image/')) || 
      format === 'image'
    );
  
  // Debug logging
  if (preview) {
    console.log('FileThumbnail render:', {
      name,
      path,
      preview,
      format,
      isVideo,
      isImage,
      fileType: file?.type
    });
  }

  const renderContent = imageView && !imageError && (
    <>
      {isImage && (
    <Box
      component="img"
      src={preview}
      onClick={() => onClick && onClick(file)}
      sx={{
        width: 1,
        height: 1,
        flexShrink: 0,
        objectFit: 'cover',
            cursor: onClick ? 'pointer' : 'default',
        ...imgSx,
      }}
      onError={handleImageError}
    />
      )}
      {isVideo && (
        <Box
          onClick={() => onClick && onClick(file)}
          sx={{
            width: 1,
            height: 1,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.900',
            cursor: onClick ? 'pointer' : 'default',
            ...imgSx,
          }}
        >
          <Box
            component="video"
            src={preview}
            sx={{
              width: 1,
              height: 1,
              objectFit: 'cover',
              flexShrink: 0,
            }}
            muted
            onMouseEnter={(e) => {
              // Show preview on hover (optional)
              e.target.play().catch(() => {});
            }}
            onMouseLeave={(e) => {
              e.target.pause();
              e.target.currentTime = 0;
            }}
          />
          {/* Play icon overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              borderRadius: '50%',
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Iconify 
              icon="solar:play-bold" 
              sx={{ 
                color: 'common.white', 
                width: 32, 
                height: 32,
                ml: 0.5 // Slight offset to center play icon visually
              }} 
            />
          </Box>
        </Box>
      )}
    </>
  );

  if (tooltip) {
    return (
      <Tooltip title={name}>
        <Stack
          flexShrink={0}
          component="span"
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 'fit-content',
            height: 'inherit',
          }}
        >
          {renderContent}
          {onDownload && <DownloadButton onDownload={onDownload} />}
        </Stack>
      </Tooltip>
    );
  }

  return (
    <>
      {renderContent}
      {onDownload && <DownloadButton onDownload={onDownload} />}
    </>
  );
}

FileThumbnail.propTypes = {
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  imageView: PropTypes.bool,
  imgSx: PropTypes.object,
  onDownload: PropTypes.func,
  sx: PropTypes.object,
  tooltip: PropTypes.bool,
  imageError: PropTypes.bool,
  setImageError: PropTypes.func,
  setValue: PropTypes.func,
  onClick: PropTypes.func,
};
